
// native
import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, finalize, map, mergeMap, takeWhile, tap } from 'rxjs/operators';

// addon
import { openDB } from 'idb';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

// services
import { PreferencesService } from './preferences.service';
import { UtilityService } from './utility.service';

// models
import { UserLoginRequest, PaginatedItems, Patient, PatientRequest, PatientJob, WorkItemFilterResponse, WorkItem } from '../../models';

// constants
import { API_LOGOUT_PATH, API_PACS_INTEGRATION_ON_PREM_PII_QUERY_PATH, AUTH_STORAGE_KEYS, DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PATIENT_JOB_STATUS } from '../../constants';

// These should not be changed for backward compatibility
const DB_NAME = 'OnPremPIIDatabase';
const STORE_NAME = 'OnPremPIIStore';
const PATIENTS_KEY_NAME = 'OnPremPIIPatients';
const LAST_UPDATED_KEY_NAME = 'OnPremPIILastUpdated';

export interface AuthToken {
  token: string;
}

export interface WorkItemsOnPremDataset {
  dataset: Partial<WorkItem>;
}

@Injectable({
  providedIn: 'root'
})
export class OnPremService {
  storeJobPageIndex = 0;

  storeQueue$ = new BehaviorSubject<any[]>([]);
  storeStart$ = new BehaviorSubject<any[]>([]);
  storeDone$ = new BehaviorSubject<any[]>([]);

  protected storeQueue: any[] = [];
  protected MAX_CONCURENT_JOBS = 1;

  constructor(
    private httpClient: HttpClient,
    private preferencesService: PreferencesService,
    private utilityService: UtilityService,
    private injector: Injector,
    private toastService: ToastrService,
    private router: Router
  ) { }

  get onPremPiiUrl(): string {
    return localStorage.getItem(AUTH_STORAGE_KEYS.ON_PREM_PII_URL);
  }

  get onPremPiiEnabled(): boolean {
    return !!this.onPremPiiUrl && !!JSON.parse(localStorage.getItem(AUTH_STORAGE_KEYS.ENABLE_ON_PREM_PII));
  }

  get httpHeaders() {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.ON_PREM_TOKEN) || '';
    return { 'X-Agent-Auth': token };
  }

  login(user: UserLoginRequest): Observable<AuthToken> {
    return this.httpClient.post(`${this.onPremPiiUrl}/v1/auth-token/`, user).pipe(map(res => res as AuthToken));
  }

  getAllPatients(pageSize: number = DEFAULT_PAGE_SIZE, pageIndex: number = DEFAULT_PAGE_INDEX, term: string = null, review: boolean = null): Observable<PaginatedItems<Patient>> {
    let path = `${this.patientsUrl}?limit=${pageSize}`;

    if (pageIndex)
      path = path + `&offset=${pageIndex * pageSize}`;

    if (term)
      path = path + `&search=${term}`;

    if (review !== null)
      path = path + `&needs_match_review=${review}`;

    return this.httpClient.get(path, { headers: this.httpHeaders }).pipe(
      catchError(error => {
        if ([401, 403].includes(error?.status))
          return this.handleOnPremError(error).pipe(
            mergeMap(() => of({ count: 0, results: [] })));

        // Fetch data from local indexDB if API fails with non-auth related error
        return from(this.getAllStoredPatients(pageSize, pageIndex, term, review));
      })
    ) as Observable<PaginatedItems<Patient>>;
  }

  getOnePatient(id: number): Observable<Patient> {
    return this.httpClient.get(`${this.patientsUrl}${id}/`, { headers: this.httpHeaders }).pipe(
      map(res => res as Patient),
      catchError(error => {
        if ([401, 403].includes(error?.status))
          return this.handleOnPremError(error);

        return from(this.getOneStoredPatient(parseInt(id as any)));
      })
    );
  }

  getPatientsByIds(ids: number[]): Observable<PaginatedItems<Patient>> {
    let path = `${this.patientsUrl}?id__in=${ids.join(',')}`;

    return this.httpClient.get(path, { headers: this.httpHeaders }).pipe(
      catchError(error => {
        return this.handleOnPremError(error).pipe(
          mergeMap(() => of({ count: 0, results: [] })));
      })
    ) as Observable<PaginatedItems<Patient>>;
  }

  showHTMLReport(path: string, windowRef: Window): void {
    // Browser window can't be opened with custom headers, so we need to fetch the report content first.
    const url = `${this.onPremPiiUrl}${path}`;
    this.httpClient.get(url, { headers: this.httpHeaders, responseType: "text" }).subscribe(
      res => {
        windowRef.document.open();
        windowRef.document.writeln(res);
        windowRef.document.close();
      }
    );
  }

  getDuplicatePatient(body: PatientRequest): Promise<Patient> {
    let path = `${this.patientsUrl}?date_of_birth=${body.date_of_birth}`;
    if (this.preferencesService.disablePi)
      path = path + `&patient_id_number=${body.patient_id_number}`;
    else
      path = path + `&first_name=${body.first_name}&last_name=${body.last_name}`;

    return this.httpClient.get(path, { headers: this.httpHeaders }).pipe(
      map((res: PaginatedItems<Patient>) => res?.results ? res.results[0] : null)
    ).toPromise();
  }

  createPatient(body: PatientRequest): Observable<Patient> {
    return this.httpClient.post(`${this.patientsUrl}`, body, { headers: this.httpHeaders }).pipe(
      map(res => res as Patient),
      tap(patient => this.saveOnPremPatient(patient)),
      catchError(err => this.handleOnPremError(err)));
  }

  deletePatient(id: number): Observable<void> {
    return this.httpClient.delete(`${this.patientsUrl}${id}/`, { headers: this.httpHeaders }).pipe(
      map(res => null),
      tap(() => this.deleteStoredOnPremPatient(id)),
      catchError(err => this.handleOnPremError(err)));
  }

  updatePatient(id: number, body: PatientRequest): Observable<Patient> {
    return this.httpClient.put(`${this.patientsUrl}${id}/`, body, { headers: this.httpHeaders }).pipe(
      map(res => res as Patient),
      tap(patient => this.saveOnPremPatient(patient)),
      catchError(err => this.handleOnPremError(err)));
  }

  mergePatient(sourceId: number, destId: number): Observable<null> {
    return this.httpClient.post(`${this.patientsUrl}${sourceId}/merge_into/`, { patient: destId }, { headers: this.httpHeaders }).pipe(
      map(res => res as null),
      catchError(err => this.handleOnPremError(err)));
  }

  filterWorkItems(body: any): Observable<WorkItemFilterResponse> {
    return this.httpClient.post(`${this.onPremPiiUrl}${API_PACS_INTEGRATION_ON_PREM_PII_QUERY_PATH}`, body, { headers: this.httpHeaders }).pipe(
      map(res => res as WorkItemFilterResponse),
      catchError(err => this.handleOnPremError(err)));
  }

  getWorkItemsPiiData(mwlQueryId: number): Promise<WorkItemsOnPremDataset[]> {
    return this.httpClient.get(`${this.onPremPiiUrl}${API_PACS_INTEGRATION_ON_PREM_PII_QUERY_PATH}${mwlQueryId}/`, { headers: this.httpHeaders }).pipe(
      map(res => res['worklist_datasets']),
      catchError(err => this.handleOnPremError(err)))
      .toPromise();
  }

  createPatientFromMwl(mwlId: string): Promise<Patient> {
    const body = { mwl_query_result_item: mwlId };
    return this.httpClient.post(`${this.patientsUrl}by_mwl/`, body, { headers: this.httpHeaders }).pipe(
      map(res => res as Patient),
    ).toPromise();
  }

  private get patientsUrl() {
    return `${this.onPremPiiUrl}/v1/patients/`;
  }

  async checkOnPremPiiStoreStatus() {
    const lastUpdatedDate = await this.getOnPremStorageLastUpdated();
    this.isOnPremStorageUpdateJobNeeded(lastUpdatedDate).subscribe(isNeeded => {
      if (isNeeded)
        this.storeOnPremPatients(lastUpdatedDate, true);
    });
  }

  private async getOnPremStorageLastUpdated(): Promise<string> {
    return await this.loadOnPremLastUpdated();
  }

  private isOnPremStorageUpdateJobNeeded(dateString: string): Observable<boolean> {
    if (!dateString)
      return of(true);

    const path = `${this.patientsUrl}?changed_after=${dateString}&limit=1`;
    return this.httpClient.get(path, { headers: this.httpHeaders }).pipe(
      map((response: PaginatedItems<Patient>) => !!response.results.length)
    );
  }

  private getOnPremOutdatedPatients(dateString: string): Observable<PaginatedItems<Patient>> {
    let path = `${this.patientsUrl}?limit=${DEFAULT_PAGE_SIZE}&offset=${this.storeJobPageIndex * DEFAULT_PAGE_SIZE}`;
    if (dateString)
      path = path + `&changed_after=${dateString}`;
    return this.httpClient.get(path, { headers: this.httpHeaders }) as Observable<PaginatedItems<Patient>>;
  }

  storeOnPremPatients(dateString: string, isInitial: boolean = false) {
    if (isInitial)
      this.storeJobPageIndex = 0;

    this.getOnPremOutdatedPatients(dateString).subscribe(patients => {
      if (!patients?.results?.length)
        return;

      patients.results.forEach(patient => {
        this.addStoreOnPremPatientJob(patient);
      });
      this.startStore(dateString);
    });
  }

  private addStoreOnPremPatientJob(patient: Patient): PatientJob {
    const action = (patient: Patient) => {
      return from(this.saveOnPremPatient(patient));
    };

    const job = new PatientJob(action, patient);
    this.storeQueue.push(job);
    return job;
  }

  private startStore(dateString: string) {
    this.continueStore(dateString);
    this.storeStart$.next(this.storeQueue);
  }

  private continueStore(dateString: string) {
    const running = this.storeQueue.filter(job => job.snapshot.status === PATIENT_JOB_STATUS.IN_PROGRESS);
    if (running.length >= this.MAX_CONCURENT_JOBS) {
      return;
    }
    const queued = this.storeQueue.filter(job => job.snapshot.status === PATIENT_JOB_STATUS.QUEUED).reverse();
    if (queued.length === 0) {
      this.storeDone$.next(this.storeQueue);
      this.storeJobPageIndex++;
      this.storeOnPremPatients(dateString);
    } else {
      const n = this.MAX_CONCURENT_JOBS - running.length;
      queued.slice(0, n).forEach(job => {
        job.status$.pipe(
          takeWhile((status: string) => ![PATIENT_JOB_STATUS.SUCCEEDED, PATIENT_JOB_STATUS.FAILED].includes(status))
        ).subscribe({
          complete: () => this.continueStore(dateString)
        });
        job.run();
      });
    }
    this.storeQueue$.next(this.storeQueue);
  }

  private async openKeyDBTransaction(mode: IDBTransactionMode) {
    const db = await openDB(DB_NAME, 1, {
      upgrade(db) { db.createObjectStore(STORE_NAME); },
    });
    return db.transaction(STORE_NAME, mode);
  }

  private async saveOnPremPatients(patients: Patient[]): Promise<void> {
    const transaction = await this.openKeyDBTransaction('readwrite');
    await transaction.store.put(patients, PATIENTS_KEY_NAME);
    await transaction.done;
  };

  async saveOnPremPatient(patient: Patient): Promise<void> {
    let storedPatients = await this.loadOnPremPatients();
    if (!storedPatients)
      storedPatients = [];

    let patientToUpdateIndex = storedPatients.findIndex(storedPatient => storedPatient.id === patient.id);
    if (patientToUpdateIndex > -1) {
      storedPatients[patientToUpdateIndex] = { ...patient };
    } else {
      storedPatients.push(patient);
    }
    await this.saveOnPremPatients(storedPatients);
    await this.saveOnPremLastUpdated(this.utilityService.convertClientDateToServerTimezoneDate(new Date()));
  }

  async deleteStoredOnPremPatient(id: number): Promise<void> {
    let storedPatients = await this.loadOnPremPatients();
    if (!storedPatients)
      return;

    let indexToDelete = storedPatients.findIndex(storedPatient => storedPatient.id === id);
    if (indexToDelete > -1) {
      storedPatients.splice(indexToDelete, 1);
    }
    await this.saveOnPremPatients(storedPatients);
    await this.saveOnPremLastUpdated(this.utilityService.convertClientDateToServerTimezoneDate(new Date()));
  }

  async loadOnPremPatients(): Promise<Patient[]> {
    const transaction = await this.openKeyDBTransaction('readonly');
    return await transaction.store.get(PATIENTS_KEY_NAME);
  };

  private async saveOnPremLastUpdated(dateString: string): Promise<void> {
    const transaction = await this.openKeyDBTransaction('readwrite');
    await transaction.store.put(dateString, LAST_UPDATED_KEY_NAME);
  };

  async loadOnPremLastUpdated(): Promise<string> {
    const transaction = await this.openKeyDBTransaction('readonly');
    return await transaction.store.get(LAST_UPDATED_KEY_NAME);
  };

  // The store holds PHI and is keyed globally (not per user/session), so it
  // must be wiped whenever the session ends or changes owner.
  async clearOnPremStore(): Promise<void> {
    const transaction = await this.openKeyDBTransaction('readwrite');
    await transaction.store.clear();
    await transaction.done;
  };

  private async getAllStoredPatients(pageSize: number, pageIndex: number, term: string, review: boolean): Promise<PaginatedItems<Patient>> {
    let storedPatients = await this.loadOnPremPatients();

    if (!storedPatients)
      storedPatients = [];

    if (term) {
      storedPatients = storedPatients.filter(patient =>
        patient.first_name.toLowerCase().includes(term.toLowerCase())
        || patient.last_name.toLowerCase().includes(term.toLowerCase())
      );
    }

    // review operations (merge patient, keep patient) are not available in case on prem service is down
    // to simplify logic, stored patients will not perform needs review checks, because the feature is not available anyway
    if (!!review) {
      storedPatients = [];
    }

    // Sorting by last_test (descending) and then by id (descending) to match db sorting
    storedPatients.sort((a, b) => {
      const dateA = new Date(a.last_test).getTime();
      const dateB = new Date(b.last_test).getTime();

      if (dateB !== dateA) {
        return dateB - dateA;
      }
      return b.id - a.id;
    });

    const startIndex = pageIndex * pageSize;
    const paginatedPatients = storedPatients.slice(startIndex, startIndex + pageSize);

    return {
      results: paginatedPatients,
      count: storedPatients.length,
    };
  }

  private async getOneStoredPatient(id: number): Promise<Patient> {
    let storedPatients = await this.loadOnPremPatients();

    if (!storedPatients)
      storedPatients = [];

    return storedPatients.find(patient => patient.id === id);
  };

  handleOnPremError(error: HttpErrorResponse, isUrlUnavailable: boolean = false) {
    const translateService = this.injector.get(TranslateService);

    if (isUrlUnavailable)
      return of(<any>this.toastService.error(translateService.instant('onPremURLUnavailable'), null, { timeOut: 20000 }));

    if (![401, 403].includes(error?.status))
      return of(<any>this.toastService.error(translateService.instant('onPremServiceUnavailable')));

    return this.httpClient
      .post(API_LOGOUT_PATH, null).pipe(
        finalize(() => {
          const ssoDomain = localStorage.getItem(AUTH_STORAGE_KEYS.SSO_DOMAIN);
          localStorage.clear();
          if (ssoDomain)
            localStorage.setItem(AUTH_STORAGE_KEYS.SSO_DOMAIN, ssoDomain);

          // This path forces a logout, so the cached PHI must go too.
          this.clearOnPremStore().catch(() => { });

          this.router.navigate(['/login']);
          this.toastService.error(translateService.instant('onPremAuthInvalid'));
        })
      );
  }
}
